import * as React from 'react';

import { DEFAULT_DESIGN_SETTINGS, type IMailDesignSettings, type MailDesignCode } from '../../api/mail-design-contracts';
import { mailDesignsApi } from '../../api/mail-designs-api';
import { useAdminData } from '../../components/admin-page';

/**
 * How the body editor draws text (SR-523 stage 6): with the real colours and font of the design
 * the mail is sent in — what the compiled body says are sentinels survey-api fills (`SENTINELS`).
 * The look reaches the editor as CSS variables on its wrapper (`--mail-text`, `--mail-muted`,
 * `--mail-accent`, `--mail-heading`, `--mail-font`); without a provider it is the defaults.
 */

export interface IEditorLook {
  text: string;
  muted: string;
  accent: string;
  heading: string;
  font: string;
}

export function lookOf(settings: Pick<IMailDesignSettings, 'colors' | 'font'>): IEditorLook {
  const { text, muted, accent, heading } = settings.colors;
  return { text, muted, accent, heading, font: settings.font };
}

const EditorLook = React.createContext<IEditorLook>(lookOf(DEFAULT_DESIGN_SETTINGS));

export const EditorLookProvider = EditorLook.Provider;

export function useLook(): IEditorLook {
  return React.useContext(EditorLook);
}

/** The wrapper's variables for the look in force. */
export function useLookVars(): React.CSSProperties {
  const look = useLook();
  return {
    '--mail-text': look.text,
    '--mail-muted': look.muted,
    '--mail-accent': look.accent,
    '--mail-heading': look.heading,
    '--mail-font': look.font
  } as React.CSSProperties;
}

/** The designs a template may name, and the current settings of each; a read that fails (an
 *  older server) leaves the built-in two at their defaults. */
export function useDesigns() {
  const list = useAdminData((signal) => mailDesignsApi.list(signal), []);
  const veritant = useAdminData((signal) => mailDesignsApi.get('veritant', signal), []);
  return React.useMemo(() => {
    const listed = list.data ?? [];
    const choices = listed.length
      ? listed.map((design) => ({ value: design.code, label: design.name || design.code }))
      : [
          { value: 'veritant', label: 'Veritant' },
          { value: 'plain', label: 'Plain' }
        ];
    const settingsOf = (code: MailDesignCode): IMailDesignSettings =>
      (code === 'veritant' ? veritant.data?.settings : undefined) ??
      listed.find((design) => design.code === code)?.current?.settings ??
      DEFAULT_DESIGN_SETTINGS;
    return { choices, settingsOf };
  }, [list.data, veritant.data]);
}
